import React from 'react';

import {
  Link,
  View,
  Actions,
  Action,
  Section,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ContentCopyIcon,
} from '@gogame/components';
import { useChildComponent, useProcessAsync } from '@gogame/hooks-ts';

import { CampaignDetails } from 'scenes/Campaigns/GGCampaigns/components/CampaignDetails';
import {
  useCopyCampaignName,
  useCopyCampaignId,
  useCopyGGCampaignLink,
  useCopyCampaignHybridLink,
  useCopyCampaignCustomLink,
} from 'scenes/Influencers/InfluencersCampaigns/hooks';

import GGLinkPopover from './GGLinkPopover';

const GGCampaigns = ({ data, hybridLink, onChange }) => {
  const [loading, processAsync] = useProcessAsync();
  const [childComponent, openChildComponent] = useChildComponent();
  const handleCopyCampaignId = useCopyCampaignId(processAsync);
  const handleCopyCampaignName = useCopyCampaignName(processAsync);
  const handleCopyGGCampaignLink = useCopyGGCampaignLink(processAsync);
  const handleCopyCampaignHybridLink = useCopyCampaignHybridLink(processAsync);
  const handleCopyCampaignCustomLink = useCopyCampaignCustomLink(processAsync);

  const handleOpenCampaign = (id) => {
    openChildComponent(CampaignDetails, {
      id,
      onChange: onChange,
    });
  };

  return (
    <View loading={loading}>
      <Section
        title={`GG Campaigns (${data?.length})`}
        isCollapsable={true}
        initialOpen={data?.length}
        action={
          <Actions>
            {hybridLink ? (
              <Action
                button
                onClick={() => handleCopyCampaignHybridLink({ hybridLink })}
                label={'Copy hybrid link'}
                tooltip={
                  'The hybrid link is generated with the first packages from the Dynamic and Conditional Rewards list.'
                }
              />
            ) : null}
          </Actions>
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell col={'id'} width={80}>
                  ID
                </TableCell>
                <TableCell col={'link'}>Name</TableCell>
                <TableCell col={'action'} width={88}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map(
                (
                  {
                    id,
                    name,
                    ggLink,
                    trackingLink,
                    additionalParameters,
                    visibleParameters,
                  },
                  i
                ) => (
                  <TableRow key={`agency_${i}`}>
                    <TableCell col={'id'}>{id}</TableCell>
                    <TableCell col={'link'}>
                      <Link
                        onClick={() => handleOpenCampaign(id)}
                        style={{ wordBreak: 'break-all' }}
                      >
                        {name}
                      </Link>
                    </TableCell>
                    <TableCell col={'actions'} padding={'actions'}>
                      <Actions edge={'start'}>
                        <Action
                          custom={
                            <GGLinkPopover
                              ggLink={ggLink}
                              trackingLink={trackingLink}
                              additionalParameters={additionalParameters}
                              visibleParameters={visibleParameters}
                            />
                          }
                        />
                        <Action icon={<ContentCopyIcon />}>
                          {ggLink ? (
                            <Action
                              onClick={() =>
                                handleCopyGGCampaignLink({ link: ggLink })
                              }
                              label={'Copy URL'}
                              button
                            />
                          ) : trackingLink ? (
                            <Action
                              onClick={() =>
                                handleCopyCampaignCustomLink({
                                  customLink: trackingLink,
                                })
                              }
                              button
                              label={'Copy custom URL'}
                            />
                          ) : null}
                          <Action
                            label={'Copy ID'}
                            onClick={() => handleCopyCampaignId({ id })}
                          />
                          <Action
                            onClick={() => handleCopyCampaignName({ name })}
                            label={'Copy name'}
                          />
                        </Action>
                      </Actions>
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {childComponent}
      </Section>
    </View>
  );
};

export default GGCampaigns;
